import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ChevronRight as ChevronRightIcon,
  FileDownload as FileDownloadIcon,
  ImageOutlined as ImageOutlinedIcon,
} from '@mui/icons-material';
import { WAIT_STEP_STATUS } from '../Step00_Wait/Wait_Schema';
import {
  FormatGaesunComDateTime,
  ResolveImprovementPhotoSrc,
} from './Improvement_Schema';

const HEADER_GREEN = '#2CA900';
const HEADER_GREEN_DARK = '#217F00';
const ACCENT_GREEN = '#2CA900';
const CARD_BORDER = 'rgba(0,0,0,0.08)';

/** 테이블(개선 보고서 카드) — 첨부 시안 색상 */
const TABLE_HEADER_BG = '#F8F9FA';
const TABLE_HEADER_TEXT = '#212529';
const TABLE_CELL_BORDER = '#DEE2E6';
const PROCESS_BADGE_BG = '#F1F3F5';
const PROCESS_BADGE_TEXT = '#495057';
const IMG_PLACEHOLDER_BG = '#F1F3F5';
const BEFORE_TEXT_BOX_BG = '#FFF5F5';
const BEFORE_TITLE_COLOR = '#E03131';
const AFTER_TEXT_BOX_BG = '#F0FFF4';
const AFTER_TITLE_COLOR = '#2F9E44';

const TABLE_CELL_SX = {
  border: `1px solid ${TABLE_CELL_BORDER}`,
};

/** 스텝 원 세로 중앙에 맞춘 연결선 — RiskAssessment / SafetyInfo 와 동일 */
const STEPPER_RAIL_TOP_PX = 16;
const CURRENT_STEP_ID = 'improvement';
const CURRENT_STEP_INDEX = 2;

const ImprovementPhotoBlock = ({ src, caption, variant }) => {
  const url = ResolveImprovementPhotoSrc(src);
  const [imgError, setImgError] = useState(false);
  useEffect(() => {
    setImgError(false);
  }, [url]);
  const isBefore = variant === 'before';
  const titleColor = isBefore ? BEFORE_TITLE_COLOR : AFTER_TITLE_COLOR;
  const textBoxBg = isBefore ? BEFORE_TEXT_BOX_BG : AFTER_TEXT_BOX_BG;
  const title = isBefore ? '위험발생상황 및 결과' : '감소대책 내용';

  return (
    <Stack
      spacing={1.25}
      alignItems="stretch"
      sx={{ maxWidth: 360, mx: 'auto', py: 0.5, width: '100%' }}
    >
      <Box
        sx={{
          width: '100%',
          minHeight: 96,
          maxHeight: 130,
          borderRadius: 2,
          bgcolor: IMG_PLACEHOLDER_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {url && !imgError ? (
          <Box
            component="img"
            src={url}
            alt=""
            onError={() => setImgError(true)}
            sx={{
              maxWidth: '100%',
              maxHeight: 130,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <Stack alignItems="center" spacing={0.75} sx={{ py: 2, px: 1 }}>
            <ImageOutlinedIcon sx={{ fontSize: 28, color: '#ADB5BD' }} />
            <Typography
              variant="body2"
              sx={{
                color: '#868E96',
                fontWeight: 500,
                textAlign: 'center',
                fontSize: '0.78rem',
              }}
            >
              등록된 사진 없음
            </Typography>
          </Stack>
        )}
      </Box>
      <Box
        sx={{
          textAlign: 'left',
          width: '100%',
          bgcolor: textBoxBg,
          borderRadius: 2,
          p: 0.9,
          mt: 0.35,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 800,
            mb: 0.45,
            color: titleColor,
            letterSpacing: '-0.02em',
            fontSize: '0.72rem',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: '#212529',
            lineHeight: 1.35,
          }}
        >
          {String(caption ?? '').trim() ? String(caption) : '—'}
        </Typography>
      </Box>
    </Stack>
  );
};

/**
 * 개선보고서 작성 화면
 */
function Improvement({
  assessmentTitle = '',
  assessmentRsCd = null,
  rows = [],
  loading = false,
  workflowSteps = [],
  onBack,
  onSaveAndNext,
  onExcelDownload,
  excelDownloading = false,
  excelDownloadDisabled = false,
}) {
  const stepList = Array.isArray(workflowSteps) ? workflowSteps : [];
  const currentStepIndexFromId = stepList.findIndex(
    (s) => (s?.routeStepId || s?.stepId) === CURRENT_STEP_ID
  );
  const currentStepIndex =
    currentStepIndexFromId >= 0
      ? currentStepIndexFromId
      : stepList.length > 0
        ? Math.min(CURRENT_STEP_INDEX, stepList.length - 1)
        : -1;

  const nextStepTitle = stepList[currentStepIndex + 1]?.title ?? '다음 단계';
  const [selectedDeptNm, setSelectedDeptNm] = useState('');
  const [selectedProcessNm, setSelectedProcessNm] = useState('');

  const departmentList = useMemo(() => {
    const names = new Set();
    (Array.isArray(rows) ? rows : []).forEach((r) => {
      names.add(String(r?.depNm ?? '').trim() || '미지정');
    });
    return Array.from(names);
  }, [rows]);

  const processListByDept = useMemo(() => {
    const names = new Set();
    (Array.isArray(rows) ? rows : []).forEach((r) => {
      const dep = String(r?.depNm ?? '').trim() || '미지정';
      if (dep !== selectedDeptNm) return;
      names.add(String(r?.processNm ?? '').trim() || '미지정');
    });
    return Array.from(names);
  }, [rows, selectedDeptNm]);

  const filteredRows = useMemo(() => {
    return (Array.isArray(rows) ? rows : []).filter((r) => {
      const dep = String(r?.depNm ?? '').trim() || '미지정';
      const proc = String(r?.processNm ?? '').trim() || '미지정';
      if (dep !== selectedDeptNm) return false;
      // 공정 미선택이면(전체) 해당 부서의 모든 공정 표시
      if (!selectedProcessNm) return true;
      return proc === selectedProcessNm;
    });
  }, [rows, selectedDeptNm, selectedProcessNm]);

  useEffect(() => {
    if (departmentList.length === 0) {
      if (selectedDeptNm) setSelectedDeptNm('');
      return;
    }
    if (!departmentList.includes(selectedDeptNm)) {
      setSelectedDeptNm(departmentList[0]);
    }
  }, [departmentList, selectedDeptNm]);

  useEffect(() => {
    if (processListByDept.length === 0) {
      if (selectedProcessNm) setSelectedProcessNm('');
      return;
    }
    // 부서 변경 시에는 공정 전체 보기(미선택) 기본
    if (selectedProcessNm && !processListByDept.includes(selectedProcessNm)) {
      setSelectedProcessNm('');
    }
  }, [processListByDept, selectedProcessNm]);

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 'calc(100dvh - 88px)',
        height: 'calc(100dvh - 88px)',
        maxHeight: 'calc(100dvh - 88px)',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        bgcolor: '#f0f4f0',
      }}
    >
      {loading ? (
        <LinearProgress
          sx={{ position: 'sticky', top: 0, zIndex: 20, height: 3 }}
        />
      ) : null}

      <Paper
        elevation={0}
        square
        sx={{
          bgcolor: HEADER_GREEN,
          color: '#fff',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={800}
          sx={{ letterSpacing: -0.2 }}
        >
          개선보고서 작성
        </Typography>
        {assessmentTitle ? (
          <Typography
            variant="caption"
            sx={{ opacity: 0.92, mt: 0.75, display: 'block' }}
          >
            {assessmentTitle}
          </Typography>
        ) : null}
      </Paper>

      {/* 스텝퍼 — GetRisCustom00R1 대시보드 스텝 (RiskAssessment / SafetyInfo 와 동일) */}
      {stepList.length > 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: { xs: 1.5, sm: 3 },
            pt: 1.5,
            pb: 1.25,
            flexShrink: 0,
            overflowX: 'auto',
            bgcolor: '#FAFBFC',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              minWidth: { xs: 640, sm: 'auto' },
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: { xs: '5%', sm: '6%' },
                right: { xs: '5%', sm: '6%' },
                top: STEPPER_RAIL_TOP_PX,
                height: 2,
                bgcolor: '#D6DCE4',
                borderRadius: 1,
                zIndex: 0,
              }}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ position: 'relative', zIndex: 1, width: '100%' }}
            >
              {stepList.map((step, index) => {
                const isActive =
                  currentStepIndex >= 0 && index === currentStepIndex;
                const isDone =
                  step?.status === WAIT_STEP_STATUS.completed && !isActive;
                const isPast =
                  isDone || (currentStepIndex >= 0 && index < currentStepIndex);
                return (
                  <Stack
                    key={String(step.stepId ?? step.routeStepId ?? index)}
                    alignItems="center"
                    sx={{
                      flex: '1 1 0',
                      minWidth: 0,
                      px: { xs: 0.25, sm: 0.5 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        flexShrink: 0,
                        bgcolor: isActive
                          ? '#0F9D00'
                          : isPast
                            ? '#DDF3D7'
                            : '#F7F9FC',
                        color: isActive || isPast ? '#fff' : '#667085',
                        border: isActive
                          ? '2px solid #0A6B00'
                          : isPast
                            ? '2px solid #4FA73D'
                            : '2px solid #C6CED8',
                        boxShadow: isActive
                          ? '0 3px 12px rgba(15, 157, 0, 0.50)'
                          : isPast
                            ? '0 1px 6px rgba(79, 167, 61, 0.30)'
                            : '0 1px 2px rgba(0,0,0,0.05)',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography
                      variant="caption"
                      align="center"
                      sx={{
                        mt: 0.6,
                        fontWeight: isActive ? 800 : isPast ? 700 : 500,
                        color: isActive
                          ? '#0A6B00'
                          : isPast
                            ? '#267A1A'
                            : '#98A2B3',
                        lineHeight: 1.35,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        width: '100%',
                        maxWidth: 108,
                      }}
                    >
                      {step.title}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </Paper>
      ) : null}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            borderColor: CARD_BORDER,
            bgcolor: '#fff',
            boxShadow: '0 6px 18px rgba(16, 24, 40, 0.06)',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${CARD_BORDER}`,
              bgcolor: '#fafbfa',
            }}
          >
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                fontWeight={800}
                color="text.primary"
                sx={{
                  fontSize: { xs: '1.05rem', sm: '1.2rem' },
                  lineHeight: 1.2,
                }}
              >
                개선 보고서
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                excelDownloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FileDownloadIcon sx={{ fontSize: 18 }} />
                )
              }
              onClick={() => onExcelDownload?.()}
              disabled={
                loading ||
                excelDownloading ||
                !assessmentRsCd ||
                excelDownloadDisabled
              }
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                flexShrink: 0,
                borderColor: HEADER_GREEN_DARK,
                color: HEADER_GREEN_DARK,
                '&:hover': {
                  borderColor: HEADER_GREEN,
                  bgcolor: 'rgba(44, 169, 0, 0.06)',
                },
                '&.Mui-disabled': {
                  borderColor: 'rgba(0,0,0,0.12)',
                },
              }}
            >
              엑셀다운로드
            </Button>
          </Stack>

          {loading ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
              }}
            >
              <CircularProgress size={40} sx={{ color: HEADER_GREEN }} />
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 1.25,
                p: 1.25,
                overflow: 'hidden',
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  width: { xs: '100%', md: 560 },
                  minWidth: { md: 460 },
                  flexShrink: 0,
                  borderColor: CARD_BORDER,
                  borderRadius: 1.5,
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: { xs: 220, md: 0 },
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  sx={{ px: 0.5, pb: 0.75 }}
                >
                  부서 / 공정
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      flex: 0.65,
                      borderColor: 'rgba(0,0,0,0.08)',
                      borderRadius: 1.25,
                      overflowY: 'auto',
                      p: 0.6,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ px: 0.5, pb: 0.5, display: 'block' }}
                    >
                      부서
                    </Typography>
                    <Stack spacing={0.5}>
                      {departmentList.map((dep) => {
                        const active = dep === selectedDeptNm;
                        return (
                          <Box
                            key={dep}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedDeptNm(dep)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                setSelectedDeptNm(dep);
                              }
                            }}
                            sx={{
                              px: 0.9,
                              py: 0.75,
                              borderRadius: 1.1,
                              border: '1px solid',
                              borderColor: active
                                ? '#81C784'
                                : 'rgba(0,0,0,0.08)',
                              bgcolor: active ? '#E8F5E9' : '#fff',
                              cursor: 'pointer',
                              '&:hover': { borderColor: '#A5D6A7' },
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={active ? 700 : 500}
                              noWrap
                            >
                              {dep}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      flex: 1.35,
                      borderColor: 'rgba(0,0,0,0.08)',
                      borderRadius: 1.25,
                      overflowY: 'auto',
                      p: 0.6,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ px: 0.5, pb: 0.5, display: 'block' }}
                    >
                      공정
                    </Typography>
                    <Stack spacing={0.5}>
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedProcessNm('')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            setSelectedProcessNm('');
                          }
                        }}
                        sx={{
                          px: 1.05,
                          py: 0.8,
                          borderRadius: 1.2,
                          border: '1px solid',
                          borderColor: !selectedProcessNm
                            ? '#90CAF9'
                            : 'rgba(0,0,0,0.08)',
                          bgcolor: !selectedProcessNm ? '#E3F2FD' : '#fff',
                          cursor: 'pointer',
                          '&:hover': { borderColor: '#64B5F6' },
                        }}
                      >
                        <Typography variant="body2" fontWeight={!selectedProcessNm ? 700 : 500}>
                          전체 공정
                        </Typography>
                      </Box>
                      {processListByDept.map((proc) => {
                        const active = proc === selectedProcessNm;
                        return (
                          <Box
                            key={proc}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedProcessNm(proc)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                setSelectedProcessNm(proc);
                              }
                            }}
                            sx={{
                              px: 1.05,
                              py: 0.8,
                              borderRadius: 1.2,
                              border: '1px solid',
                              borderColor: active
                                ? '#90CAF9'
                                : 'rgba(0,0,0,0.08)',
                              bgcolor: active ? '#E3F2FD' : '#fff',
                              cursor: 'pointer',
                              '&:hover': { borderColor: '#64B5F6' },
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={active ? 700 : 500}
                            >
                              {proc}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Paper>
                </Box>
              </Paper>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <TableContainer
                  sx={{
                    flex: '1 1 0',
                    minHeight: 0,
                    height: 0,
                    maxHeight: '100%',
                    overflow: 'auto',
                  }}
                >
                  <Table
                    size="small"
                    stickyHeader
                    sx={{
                      width: '100%',
                      tableLayout: 'fixed',
                      borderCollapse: 'collapse',
                    }}
                  >
                    <colgroup>
                      <col style={{ width: 28 }} />
                  <col style={{ width: 150 }} />
                      <col style={{ width: '31%' }} />
                      <col style={{ width: '31%' }} />
                  <col style={{ width: 130 }} />
                    </colgroup>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="center"
                          sx={{
                            ...TABLE_CELL_SX,
                            fontWeight: 800,
                            bgcolor: TABLE_HEADER_BG,
                            color: TABLE_HEADER_TEXT,
                            width: 28,
                            minWidth: 28,
                            maxWidth: 28,
                            px: 0,
                          }}
                        >
                          NO
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...TABLE_CELL_SX,
                            fontWeight: 800,
                            bgcolor: TABLE_HEADER_BG,
                            color: TABLE_HEADER_TEXT,
                        width: 150,
                        minWidth: 150,
                            px: 0.5,
                          }}
                        >
                          부서 / 공정
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...TABLE_CELL_SX,
                            fontWeight: 800,
                            bgcolor: TABLE_HEADER_BG,
                            color: TABLE_HEADER_TEXT,
                            width: '34%',
                            minWidth: 220,
                          }}
                        >
                          개선 전 (BEFORE)
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...TABLE_CELL_SX,
                            fontWeight: 800,
                            bgcolor: TABLE_HEADER_BG,
                            color: TABLE_HEADER_TEXT,
                            width: '34%',
                            minWidth: 220,
                          }}
                        >
                          개선 후 (AFTER)
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...TABLE_CELL_SX,
                            fontWeight: 800,
                            bgcolor: TABLE_HEADER_BG,
                            color: TABLE_HEADER_TEXT,
                            width: '10%',
                        minWidth: 130,
                          }}
                        >
                          완료일자
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={{
                              ...TABLE_CELL_SX,
                              py: 6,
                              color: 'text.secondary',
                            }}
                          >
                            선택한 부서/공정의 데이터가 없습니다.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredRows.map((row, idx) => (
                          <TableRow
                            key={row.clientKey ?? row.rsdCd ?? idx}
                            hover
                          >
                            <TableCell
                              align="center"
                              sx={{
                                ...TABLE_CELL_SX,
                                verticalAlign: 'middle',
                                fontWeight: 700,
                                color: TABLE_HEADER_TEXT,
                                width: 28,
                                minWidth: 28,
                                maxWidth: 28,
                                px: 0,
                              }}
                            >
                              {idx + 1}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ ...TABLE_CELL_SX, verticalAlign: 'middle' }}
                            >
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              justifyContent: 'center',
                              alignItems: 'center',
                              columnGap: 0.75,
                              rowGap: 1,
                              py: 0.35,
                            }}
                          >
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    bgcolor: '#EEF2FF',
                                    color: '#374151',
                                    borderRadius: 999,
                                    px: 1.3,
                                    py: 0.45,
                                    fontWeight: 700,
                                    fontSize: '0.78rem',
                                    maxWidth: '100%',
                                    textAlign: 'center',
                                  }}
                                >
                                  {row.depNm || '—'}
                                </Box>
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    bgcolor: PROCESS_BADGE_BG,
                                    color: PROCESS_BADGE_TEXT,
                                    borderRadius: 999,
                                    px: 1.5,
                                    py: 0.55,
                                    fontWeight: 700,
                                    fontSize: '0.84rem',
                                    maxWidth: '100%',
                                    textAlign: 'center',
                                  }}
                                >
                                  {row.processNm || '—'}
                                </Box>
                          </Box>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ ...TABLE_CELL_SX, verticalAlign: 'top' }}
                            >
                              <ImprovementPhotoBlock
                                variant="before"
                                src={row.riskImageFilePath}
                                caption={row.categoryComment}
                              />
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ ...TABLE_CELL_SX, verticalAlign: 'top' }}
                            >
                              <ImprovementPhotoBlock
                                variant="after"
                                src={row.afterRiskImageFilePath}
                                caption={row.reductionMeasures}
                              />
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                ...TABLE_CELL_SX,
                                verticalAlign: 'middle',
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: TABLE_HEADER_TEXT,
                                  fontWeight: 600,
                                  lineHeight: 1.4,
                                  wordBreak: 'break-word',
                                }}
                              >
                                {FormatGaesunComDateTime(row.gaesunComDate)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* 하단 푸터 — SafetyInfo / RiskAssessment 와 동일 패턴, 완료 버튼명만 변경 */}
      <Paper
        elevation={0}
        square
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          flexShrink: 0,
          bgcolor: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              alignSelf: { xs: 'stretch', sm: 'auto' },
            }}
          >
            이전 단계
          </Button>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              다음 단계: {nextStepTitle}
            </Typography>
            <Button
              variant="contained"
              onClick={onSaveAndNext}
              endIcon={<ChevronRightIcon />}
              disabled={loading || !assessmentRsCd}
              fullWidth={false}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                px: 3,
                bgcolor: ACCENT_GREEN,
                '&:hover': { bgcolor: HEADER_GREEN_DARK },
                alignSelf: { xs: 'stretch', sm: 'auto' },
              }}
            >
              위험성평가 완료
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

export default Improvement;
